import { type AnalyticsDailyRow } from '@carsfinders/contracts';

import { t } from '../../../lib/i18n';
import { shiftIsoDay } from '../format';

export interface KpiTotals {
  sessions: number;
  uniqueVisitors: number;
  uniqueUsers: number;
  pageViews: number;
}

export interface KpiColumn {
  key: 'today' | 'd7' | 'd30';
  totals: KpiTotals;
}

const ZERO: KpiTotals = { sessions: 0, uniqueVisitors: 0, uniqueUsers: 0, pageViews: 0 };

function sumSince(days: readonly AnalyticsDailyRow[], since: string, today: string): KpiTotals {
  return days
    .filter((row) => row.date >= since && row.date <= today)
    .reduce<KpiTotals>(
      (acc, row) => ({
        sessions: acc.sessions + row.sessions,
        uniqueVisitors: acc.uniqueVisitors + row.uniqueVisitors,
        uniqueUsers: acc.uniqueUsers + row.uniqueUsers,
        pageViews: acc.pageViews + row.pageViews,
      }),
      ZERO,
    );
}

/**
 * Сьогодні / 7 днів / 30 днів out of ONE 30-day summary: today is the last day,
 * 7 is the sum of the last seven. Uniques over a period are therefore the SUM
 * of daily uniques (the rollup keeps no visitor sets) — the tile says so.
 */
export function buildKpiColumns(days: readonly AnalyticsDailyRow[], today: string): KpiColumn[] {
  return [
    { key: 'today', totals: sumSince(days, today, today) },
    { key: 'd7', totals: sumSince(days, shiftIsoDay(today, -6), today) },
    { key: 'd30', totals: sumSince(days, shiftIsoDay(today, -29), today) },
  ];
}

const METRICS: readonly (keyof KpiTotals)[] = ['sessions', 'uniqueVisitors', 'uniqueUsers', 'pageViews'];

const NUMBER = new Intl.NumberFormat('uk-UA');

export function KpiTiles({ columns }: { columns: KpiColumn[] }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:gap-3" data-testid="analytics-kpi">
      {columns.map((column) => (
        <section
          key={column.key}
          data-kpi={column.key}
          className="rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 shadow-[inset_0_3px_0_var(--color-cf-accent)] lg:px-[18px] lg:py-4"
        >
          <h2 className="font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:text-[11px]">
            {t(`admin.analytics.kpi.${column.key}`)}
          </h2>
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2.5">
            {METRICS.map((metric) => (
              <div key={metric} data-metric={metric}>
                <dt className="text-[11px] leading-[14px] text-cf-ink-subtle lg:text-xs">
                  {t(`admin.analytics.kpi.${metric}`)}
                </dt>
                <dd className="mt-0.5 font-mono text-[20px] leading-none font-medium text-cf-ink lg:text-[24px]">
                  {NUMBER.format(column.totals[metric])}
                </dd>
                {/* A period's uniques are summed per day; a single day's are exact. */}
                {column.key !== 'today' && (metric === 'uniqueVisitors' || metric === 'uniqueUsers') && (
                  <p className="mt-0.5 text-[10px] leading-[13px] text-cf-ink-faint">
                    {t('admin.analytics.kpi.sumOfDaily')}
                  </p>
                )}
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}

/** The loading skeleton: three tile frames, nothing that pretends to be a number. */
export function KpiTilesSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:gap-3" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-[132px] animate-pulse rounded-cf-lg border border-cf-line bg-cf-surface"
        />
      ))}
    </div>
  );
}
